import React from 'react';
import {
    FileText,
    Globe,
    DollarSign,
    ShieldCheck,
    Zap,
    Heart,
    Check,
    Eye,
    XCircle
} from 'lucide-react';

/**
 * Policy Section - Pure Outcomes Focus
 *
 * Key message: Declare what you want. Forecaster enforces it.
 *
 * Showcases policy OUTCOMES (what), not implementation (how):
 * - Data sovereignty: Keep workloads in specific regions
 * - Cost guardrails: Never exceed budget limits
 * - Fault tolerance: Automatic recovery when things fail
 * - Compliance: Meet regulatory requirements automatically
 * - SLO enforcement: Guarantee latency and availability
 *
 * NO secret sauce - no CRD names, no technical details, just results.
 */

// Policy outcome card
interface PolicyOutcomeProps {
    icon: React.ElementType;
    title: string;
    outcome: string;
    example: string;
}

const PolicyOutcome = ({ icon: Icon, title, outcome, example }: PolicyOutcomeProps) => (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-volt-500/30 transition-all group">
        <div className="w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center mb-4 group-hover:bg-volt-500/20 transition-colors">
            <Icon className="w-6 h-6 text-volt-400" />
        </div>
        <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
        <p className="text-gray-400 text-sm mb-4">{outcome}</p>
        <div className="flex items-center gap-2 text-xs text-volt-400 font-medium">
            <Check className="w-3 h-3" />
            <span>{example}</span>
        </div>
    </div>
);

// Policy declaration visual
const PolicyDeclaration = () => {
    const declarations = [
        { label: 'Sovereignty', value: 'EU only', active: true },
        { label: 'Max budget', value: '$50k/mo', active: true },
        { label: 'Min availability', value: '99.9%', active: true },
        { label: 'P99 latency', value: 'Enforced', active: true },
        { label: 'Auto-recovery', value: 'Enabled', active: true },
    ];

    return (
        <div className="relative p-8 rounded-3xl border border-white/[0.06] bg-white/[0.02]">
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent rounded-3xl" />

            <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-volt-500 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-obsidian-900" />
                    </div>
                    <div>
                        <div className="text-white font-semibold">Your Policy</div>
                        <div className="text-sm text-gray-500">Set once, always enforced</div>
                    </div>
                </div>

                {/* Policy declarations */}
                <div className="space-y-3 mb-6">
                    {declarations.map((item, i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between p-3 rounded-lg bg-obsidian-800"
                        >
                            <span className="text-sm text-gray-400">{item.label}</span>
                            <span className={`text-sm font-medium ${item.active ? 'text-volt-400' : 'text-gray-500'}`}>
                                {item.value}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Bottom message */}
                <div className="p-4 rounded-xl bg-volt-500/10 border border-volt-500/20">
                    <div className="flex items-start gap-3">
                        <ShieldCheck className="w-5 h-5 text-volt-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-gray-300">
                            <span className="text-white font-medium">Declare once. </span>
                            Forecaster continuously enforces your policies across all workloads.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// What policies guarantee visual
const PolicyGuarantees = () => {
    const guarantees = [
        {
            title: 'Data never leaves region',
            description: 'Workloads stay in declared geography'
        },
        {
            title: 'Costs stay within budget',
            description: 'Hard limits, not just alerts'
        },
        {
            title: 'Failures recover automatically',
            description: 'No 3am pages for routine issues'
        },
        {
            title: 'SLOs are enforced, not hoped for',
            description: 'Latency and availability guaranteed'
        },
    ];

    return (
        <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-volt-500/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-volt-400" />
                </div>
                <div>
                    <div className="text-white font-medium">Policy Guarantees</div>
                    <div className="text-xs text-gray-500">Not best effort. Enforced.</div>
                </div>
            </div>

            <div className="space-y-4">
                {guarantees.map((g, i) => (
                    <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-volt-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-volt-400" />
                        </div>
                        <div>
                            <div className="text-sm font-medium text-white">{g.title}</div>
                            <div className="text-xs text-gray-500">{g.description}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const PolicySection = () => {
    const outcomes = [
        {
            icon: Globe,
            title: 'Data Sovereignty',
            outcome: 'Multi-Region Intelligence enforces compliance zones across all 12 providers. EU, US, HIPAA.',
            example: 'GDPR workloads route only to EU regions — across every provider',
        },
        {
            icon: DollarSign,
            title: 'Cost Guardrails',
            outcome: 'Set hard budget limits. Never wake up to a surprise cloud bill.',
            example: 'Inference spend capped at $50k/month',
        },
        {
            icon: Heart,
            title: 'Fault Tolerance',
            outcome: 'Automatic recovery when GPUs fail, nodes die, or providers have outages.',
            example: 'Workloads recover in < 60 seconds',
        },
        {
            icon: Zap,
            title: 'SLO Enforcement',
            outcome: 'Guarantee latency and availability. Not best effort—enforced.',
            example: 'P99 latency targets enforced automatically',
        },
    ];

    return (
        <section className="py-24 lg:py-32 bg-obsidian-900" id="policy">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                        <FileText className="w-4 h-4" />
                        AI Factory Control
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                        Declare intent.
                        <br />
                        <span className="text-gray-500">Forecaster enforces it.</span>
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        One policy. Full control over sovereignty, costs, availability, and compliance.
                        Stop firefighting. Start declaring.
                    </p>
                </div>

                {/* Two column layout */}
                <div className="grid lg:grid-cols-2 gap-8 mb-16">
                    {/* Left: Policy declaration visual */}
                    <PolicyDeclaration />

                    {/* Right: What policies guarantee */}
                    <div className="space-y-6">
                        <PolicyGuarantees />
                    </div>
                </div>

                {/* Outcome grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {outcomes.map((outcome, i) => (
                        <PolicyOutcome key={i} {...outcome} />
                    ))}
                </div>

                {/* Bottom insight */}
                <div className="mt-16 text-center">
                    <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                        <span className="text-white font-semibold">Your AI factory, your rules.</span>{' '}
                        Define what matters to your business. Forecaster makes it happen—
                        automatically, continuously, reliably.
                    </p>
                </div>

                {/* How it behaves in your cluster */}
                <div className="mt-16">
                    <h3 className="text-center text-xl font-semibold text-white mb-8">How it behaves in your cluster</h3>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-volt-500/10 border border-volt-500/20 flex items-center justify-center mx-auto mb-4">
                                <Eye className="w-6 h-6 text-volt-400" />
                            </div>
                            <h4 className="text-white font-medium mb-2">Watches metrics</h4>
                            <p className="text-gray-400 text-sm">
                                Read-only access to workloads you opt in.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-volt-500/10 border border-volt-500/20 flex items-center justify-center mx-auto mb-4">
                                <ShieldCheck className="w-6 h-6 text-volt-400" />
                            </div>
                            <h4 className="text-white font-medium mb-2">Changes nothing by default</h4>
                            <p className="text-gray-400 text-sm">
                                Only acts on annotated workloads. You control scope.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-6 h-6 text-white" />
                            </div>
                            <h4 className="text-white font-medium mb-2">Uninstall anytime</h4>
                            <p className="text-gray-400 text-sm">
                                One command. Workloads continue running unchanged.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section divider */}
                <div className="max-w-xl mx-auto mt-24">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
            </div>
        </section>
    );
};
